import { browserIndexedDbFactory, createIndexedDbMetadataCache } from '@hgc/core';
import { useEffect, useState } from 'react';

import { useAppStore } from '../../runtime/AppStoreContext';
import { usePrimaryVehicle } from '../../runtime/hooks';
import { ParamMetadataLoader, paramPlatformForClass } from './metadata';
import { createParameterSession, type ParameterSession } from './session';

/**
 * 焦点机体的参数会话。
 *
 * 会话跟着**焦点机体**走，而不是跟着页签走：切到摘要页再切回来，已下载的参数表与
 * 待写入改动都还在。焦点换机（或重连后注册表重建）时旧会话立刻销毁 ——
 * 留着它，旧机体的事件总线上就还挂着一个回调，表现是"切来切去之后越来越慢"。
 */

export interface ParameterSessionHandle {
  readonly session: ParameterSession | null;
  /** 为什么现在没有会话；有会话时为 null */
  readonly unavailableReason: string | null;
}

export const WAITING_FOR_VEHICLE = '正在等待机体心跳…';
export const NO_AUTOPILOT_REASON =
  '链路上还没有飞控：参数归飞控组件所有，连上之后这一页会自动出现。';

export function useParameterSession(): ParameterSessionHandle {
  const store = useAppStore();
  const summary = usePrimaryVehicle();
  // 用摘要（稳定引用）取回 Vehicle 对象：Vehicle 本身在重连后是**新对象**，
  // 拿它当依赖正好等于"连接一变就重建会话"
  const vehicle = summary === null ? null : store.registry?.get(summary.id) ?? null;
  const vehicleClass = summary?.vehicleClass ?? null;

  const [handle, setHandle] = useState<ParameterSessionHandle>({
    session: null,
    unavailableReason: WAITING_FOR_VEHICLE,
  });

  useEffect(() => {
    if (vehicle === null) {
      setHandle({ session: null, unavailableReason: NO_AUTOPILOT_REASON });
      return;
    }

    const factory = browserIndexedDbFactory();
    const loader = new ParamMetadataLoader({
      platform: paramPlatformForClass(vehicle.state.vehicleClass),
      // 固件版本目前拿不到（心跳与状态里都没有它），所以缓存键是 `机型@unknown`。
      // 编一个版本号会让缓存命中**另一版固件**的定义文件，那比没有缓存危险。
      firmwareVersion: 'unknown',
      ...(factory === null ? {} : { cache: createIndexedDbMetadataCache(factory) }),
    });

    const session = createParameterSession({ vehicle, metadataLoader: loader });
    setHandle({ session, unavailableReason: null });
    // 元数据是"有就用"，拿不到也是正常状态（野外没有网），所以不 await
    void session.loadMetadata();

    return () => {
      session.dispose();
    };
    // 车辆类别参与依赖：心跳到达之前类别是 unknown，那时定不出元数据平台
  }, [vehicle, vehicleClass]);

  return handle;
}
